import "@maersk-global/mds-components-core-select";
import { MdsConfig } from "@maersk-global/mds-config";
import "./selects.css";

export const PHASE6_VERSION = "mds-select-mirrors";

const FIELD_DEFINITIONS = [
  { name: "shipment_type", tagName: "mc-select" },
  { name: "port_of_loading", tagName: "mc-select" },
  { name: "destination_country", tagName: "mc-select" },
  { name: "port_of_discharge", tagName: "mc-select" },
  { name: "shipment_mode", tagName: "mc-select" },
  { name: "package_type", tagName: "mc-select" },
  { name: "wooden_package", tagName: "mc-select" },
  { name: "bl_issuance", tagName: "mc-select" }
] as const;

type NativeSelect = HTMLSelectElement;
type SelectOptionData = { label: string; value: string; disabled?: boolean };
type MdsSelect = HTMLElement & {
  data?: SelectOptionData[] | null;
  value?: unknown;
  label?: string;
  placeholder?: string;
  invalid?: boolean;
  errormessage?: string;
  disabled?: boolean;
  required?: boolean;
  readonly?: boolean;
};

type FieldDefinition = (typeof FIELD_DEFINITIONS)[number];

export interface Phase6SelectBinding {
  name: string;
  mds: MdsSelect;
  native: NativeSelect;
  wrapper: HTMLLabelElement;
}

export interface Phase6Contract {
  installed: boolean;
  version: string;
  fieldNames: string[];
  bindings: Phase6SelectBinding[];
}

function configureMdsRuntime(documentRef: Document): void {
  const isViteDevServer = Boolean(documentRef.querySelector('script[src="/@vite/client"]'));
  MdsConfig.iconsDynamicImportPath = isViteDevServer
    ? "/node_modules/"
    : new URL("./assets/node_modules/", documentRef.baseURI).pathname;
}

function normalizeValue(value: unknown): string {
  return value === null || value === undefined ? "" : String(value);
}

function getMdsValue(field: MdsSelect): string {
  return normalizeValue(field.value);
}

function getLabelSpan(wrapper: HTMLLabelElement, documentRef: Document, fallback: string): HTMLElement {
  const existing = wrapper.querySelector<HTMLElement>(".label-text");
  if (existing) {
    existing.classList.add("phase6-legacy-label");
    existing.setAttribute("aria-hidden", "true");
    existing.hidden = true;
    return existing;
  }

  const textNode = Array.from(wrapper.childNodes).find(
    node => node.nodeType === Node.TEXT_NODE && Boolean(node.textContent?.trim()),
  );
  const labelSpan = documentRef.createElement("span");
  labelSpan.className = "label-text phase6-legacy-label";
  labelSpan.setAttribute("aria-hidden", "true");
  labelSpan.hidden = true;
  labelSpan.textContent = textNode?.textContent?.trim() || fallback;
  if (textNode) {
    textNode.replaceWith(labelSpan);
  } else {
    wrapper.insertBefore(labelSpan, wrapper.firstChild);
  }
  return labelSpan;
}

function getLabelText(labelSpan: HTMLElement, fallback: string): string {
  return labelSpan.textContent?.trim() || fallback;
}

function setMdsProperty(field: MdsSelect, propertyName: string, value: unknown): void {
  (field as unknown as Record<string, unknown>)[propertyName] = value;
}

function findPropertyDescriptor(target: object, propertyName: string): PropertyDescriptor | null {
  let current: object | null = target;
  while (current) {
    const descriptor = Object.getOwnPropertyDescriptor(current, propertyName);
    if (descriptor) {
      return descriptor;
    }
    current = Object.getPrototypeOf(current);
  }
  return null;
}

function bridgeValueSetter(target: object, onSet: () => void): void {
  const descriptor = findPropertyDescriptor(target, "value");
  if (!descriptor?.get || !descriptor.set) {
    return;
  }

  try {
    Object.defineProperty(target, "value", {
      configurable: true,
      enumerable: descriptor.enumerable,
      get: descriptor.get.bind(target),
      set(value: unknown) {
        descriptor.set?.call(target, value);
        onSet();
      }
    });
  } catch {
    // A browser may expose a non-configurable value accessor. Event and
    // mutation observers below still cover user interaction and option lists.
  }
}

function copyNativeAttributes(native: NativeSelect, mds: MdsSelect, definition: FieldDefinition, label: string): void {
  mds.setAttribute("name", definition.name);
  mds.setAttribute("label", label);
  mds.setAttribute("data-phase6-field", definition.name);
  mds.classList.add("phase6-mds-field");
  setMdsProperty(mds, "value", native.value);

  if (native.required) {
    mds.setAttribute("required", "");
  }
  if (native.disabled) {
    mds.setAttribute("disabled", "");
  }
}

function dispatchMirrorEvent(native: NativeSelect, eventName: "input" | "change" | "focus" | "blur"): void {
  const event = eventName === "focus" || eventName === "blur"
    ? new FocusEvent(eventName, { bubbles: true, composed: true })
    : new Event(eventName, { bubbles: true, composed: true });
  native.dispatchEvent(event);
}

function clearLegacyInvalidState(native: NativeSelect): void {
  native.classList.remove("invalid");
  native.removeAttribute("aria-invalid");
  native.closest("label")?.classList.remove("invalid-label");
}

function clearMdsInvalidState(mds: MdsSelect): void {
  mds.classList.remove("invalid");
  mds.removeAttribute("aria-invalid");
  setMdsProperty(mds, "invalid", false);
}

function setLegacyInvalidState(native: NativeSelect, invalid: boolean): void {
  native.classList.toggle("invalid", invalid);
  native.closest("label")?.classList.toggle("invalid-label", invalid);
  if (invalid) {
    native.setAttribute("aria-invalid", "true");
  } else {
    native.removeAttribute("aria-invalid");
  }
}

function queueMicrotaskCompat(callback: () => void): void {
  if (typeof queueMicrotask === "function") {
    queueMicrotask(callback);
    return;
  }
  void Promise.resolve().then(callback);
}

function syncNativeStateToMds(binding: Phase6SelectBinding): void {
  const { native, mds } = binding;
  const invalid = native.classList.contains("invalid") || native.getAttribute("aria-invalid") === "true";
  setMdsProperty(mds, "invalid", invalid);
  if (!invalid) {
    mds.classList.remove("invalid");
  }
  setMdsProperty(mds, "disabled", native.disabled);
  setMdsProperty(mds, "required", native.required);
  setMdsProperty(mds, "readonly", false);
}

function syncMdsInvalidToNative(binding: Phase6SelectBinding): void {
  const { native, mds } = binding;
  const invalid = Boolean(mds.invalid) || mds.classList.contains("invalid") || mds.getAttribute("aria-invalid") === "true";
  setLegacyInvalidState(native, invalid);
  if (Boolean(mds.invalid) !== invalid) {
    setMdsProperty(mds, "invalid", invalid);
  }
}

function readNativeOptions(native: NativeSelect): SelectOptionData[] {
  return Array.from(native.options).map(option => ({
    label: option.textContent || option.label || option.value,
    value: option.value,
    ...(option.disabled ? { disabled: true } : {})
  }));
}

function syncNativeOptionsToMds(binding: Phase6SelectBinding): void {
  const { native, mds } = binding;
  const options = readNativeOptions(native);
  const emptyOption = options.find(option => option.value === "");
  const data = options.filter(option => option.value !== "");

  setMdsProperty(mds, "data", data);
  setMdsProperty(mds, "placeholder", emptyOption?.label || "");
  if (getMdsValue(mds) !== native.value) {
    setMdsProperty(mds, "value", native.value);
  }
}

function syncMdsValueToNative(binding: Phase6SelectBinding): boolean {
  const { native, mds } = binding;
  const value = getMdsValue(mds);
  if (native.value === value) {
    return false;
  }
  native.value = value;
  return true;
}

function syncNativeValueToMds(binding: Phase6SelectBinding): boolean {
  const { native, mds } = binding;
  if (getMdsValue(mds) === native.value) {
    return false;
  }
  setMdsProperty(mds, "value", native.value);
  return true;
}

function installBindingObservers(binding: Phase6SelectBinding, labelSpan: HTMLElement): void {
  const { native, mds, wrapper } = binding;
  let lastNotifiedSelection: string | null = null;
  let selectionResetQueued = false;

  const resetSelectionMarker = (): void => {
    if (selectionResetQueued) {
      return;
    }
    selectionResetQueued = true;
    queueMicrotaskCompat(() => {
      lastNotifiedSelection = null;
      selectionResetQueued = false;
    });
  };

  const syncLabel = (): void => {
    const nextLabel = getLabelText(labelSpan, binding.name);
    if (normalizeValue(mds.label) !== nextLabel) {
      mds.setAttribute("label", nextLabel);
      setMdsProperty(mds, "label", nextLabel);
    }
  };

  const handleMdsValueEvent = (eventName: "input" | "optionselected" | "change"): void => {
    clearLegacyInvalidState(native);
    clearMdsInvalidState(mds);

    const changed = syncMdsValueToNative(binding);
    if (changed) {
      dispatchMirrorEvent(native, "input");
    }

    if (eventName !== "change" && lastNotifiedSelection === getMdsValue(mds)) {
      return;
    }

    if (eventName !== "change") {
      lastNotifiedSelection = getMdsValue(mds);
      resetSelectionMarker();
    }
    dispatchMirrorEvent(native, "change");
  };

  const mdsObserver = new MutationObserver(() => {
    syncLabel();
    syncMdsInvalidToNative(binding);
  });
  mdsObserver.observe(mds, { attributes: true, childList: true, subtree: true });

  const nativeObserver = new MutationObserver(() => {
    syncNativeOptionsToMds(binding);
    syncNativeStateToMds(binding);
  });
  nativeObserver.observe(native, {
    attributes: true,
    attributeFilter: ["aria-invalid", "class", "disabled", "required", "readonly"],
    childList: true,
    subtree: true
  });

  const labelObserver = new MutationObserver(syncLabel);
  labelObserver.observe(wrapper, { characterData: true, childList: true, subtree: true });

  native.addEventListener("input", () => {
    syncNativeOptionsToMds(binding);
    syncNativeStateToMds(binding);
    syncNativeValueToMds(binding);
  });
  native.addEventListener("change", () => {
    syncNativeOptionsToMds(binding);
    syncNativeStateToMds(binding);
    syncNativeValueToMds(binding);
  });
  mds.addEventListener("input", () => handleMdsValueEvent("input"));
  mds.addEventListener("optionselected", () => handleMdsValueEvent("optionselected"));
  mds.addEventListener("change", () => handleMdsValueEvent("change"));
  mds.addEventListener("focus", () => dispatchMirrorEvent(native, "focus"));
  mds.addEventListener("blur", () => dispatchMirrorEvent(native, "blur"));

  const shadowRoot = mds.shadowRoot;
  shadowRoot?.addEventListener("focus", () => dispatchMirrorEvent(native, "focus"), true);
  shadowRoot?.addEventListener("blur", () => dispatchMirrorEvent(native, "blur"), true);

  bridgeValueSetter(native, () => {
    queueMicrotaskCompat(() => {
      syncNativeOptionsToMds(binding);
      syncNativeValueToMds(binding);
    });
  });
  bridgeValueSetter(mds, () => {
    queueMicrotaskCompat(() => syncMdsValueToNative(binding));
  });

  syncLabel();
  syncNativeOptionsToMds(binding);
  syncNativeStateToMds(binding);
  syncNativeValueToMds(binding);
}

function createBinding(
  form: HTMLFormElement,
  definition: FieldDefinition,
  documentRef: Document,
): Phase6SelectBinding | null {
  const native = form.querySelector<NativeSelect>(`select[name="${definition.name}"]:not([data-phase6-native-mirror])`);
  if (!native) {
    return null;
  }
  const wrapper = native.closest("label");
  if (!wrapper) {
    return null;
  }

  const labelSpan = getLabelSpan(wrapper, documentRef, definition.name);
  const mds = documentRef.createElement(definition.tagName) as MdsSelect;
  copyNativeAttributes(native, mds, definition, getLabelText(labelSpan, definition.name));

  native.classList.add("phase6-native-mirror");
  native.setAttribute("data-phase6-native-mirror", definition.name);
  native.setAttribute("aria-hidden", "true");
  native.tabIndex = -1;
  native.hidden = true;
  wrapper.insertBefore(mds, native);

  const binding: Phase6SelectBinding = { name: definition.name, mds, native, wrapper };
  installBindingObservers(binding, labelSpan);
  return binding;
}

export function installPhase6Selects(documentRef: Document = document): Phase6Contract {
  if (!documentRef?.body) {
    return { installed: false, version: PHASE6_VERSION, fieldNames: [], bindings: [] };
  }

  const form = documentRef.querySelector<HTMLFormElement>("#draftForm");
  if (!form) {
    return { installed: false, version: PHASE6_VERSION, fieldNames: [], bindings: [] };
  }

  configureMdsRuntime(documentRef);
  documentRef.body.classList.add("phase6-selects");
  const bindings = FIELD_DEFINITIONS.map(definition => createBinding(form, definition, documentRef)).filter(
    (binding): binding is Phase6SelectBinding => Boolean(binding),
  );
  const contract: Phase6Contract = {
    installed: bindings.length === FIELD_DEFINITIONS.length,
    version: PHASE6_VERSION,
    fieldNames: bindings.map(binding => binding.name),
    bindings
  };

  return contract;
}

const phase6Contract =
  typeof document === "undefined"
    ? { installed: false, version: PHASE6_VERSION, fieldNames: [], bindings: [] }
    : installPhase6Selects(document);

(globalThis as typeof globalThis & { __newformsPhase6?: Phase6Contract }).__newformsPhase6 = phase6Contract;
